import { Component, effect, ElementRef, inject, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { SiteFieldHighlightService } from 'src/app/services/app/site-field-highlight.service';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BinderService } from '@/services/api/binder.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { BinderDeliverablesService } from '@/services/app/binder-deliverables.service';
import { EngineeringFlaggingService } from '@/services/app/engineering-flagging.service';
import { states } from '@/services/data/states';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

interface Warning {
  severity: string,
  text: string
}

@Component({
  selector: 'app-binder-site-editor',
  imports: [
    CommonModule, 
    SelectModule, 
    SelectButtonModule, 
    ButtonModule, 
    InputTextModule, 
    InputNumberModule, 
    ReactiveFormsModule, 
    SelectModule, 
    InputGroupModule, 
    InputGroupAddonModule,
    InputGroupAddonModule
  ],
  template: `
    <div [formGroup]="editForm">
    <div class="flex mt-8">
            <div class="card flex flex-col gap-4 w-full">
                <div class="flex flex-wrap">
                    <label for="address">Address</label>
                    <input pInputText id="address" formControlName="SiteAddress" class="w-full"/>
                </div>

                <div class="flex flex-col md:flex-row gap-4">
                  <div class="flex flex-wrap gap-2 w-full">
                        <label for="city">City</label>
                        <input pInputText id="city" type="text" formControlName="SiteCity" class="w-full"/>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <label for="state">State</label>
                        <p-select id="state" [options]="states" 
                    formControlName="SiteState"
                    optionLabel="shortName"
                    optionValue="shortName"
                    placeholder="Select a State"
                    (onChange)="setSelectedState($event.value)"
                     class="w-full"></p-select>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <label for="zip">Zip</label>
                        <input pInputText id="zip" type="number" [max]="99999" [min]="0" formControlName="SiteZip" class="w-full"/>
                    </div>
                </div>

                <div class="flex flex-wrap">
                    <label for="parcel">Parcel</label>
                    <input pInputText id="parcel" type="text" formControlName="SitePlot" class="w-full"/>
                </div>
                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                        <label for="county">Jurisdiction</label>
                        <input pInputText id="county" type="text" formControlName="County" class="w-full"/>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <label for="taxmode">Tax Mode</label>
                        <p-selectButton [allowEmpty]="false" [options]="taxModes" id="taxmode" formControlName="TaxOverride" optionLabel="label" optionValue="value" class="w-full"></p-selectButton>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <label [hidden]="!taxBool()" #highlightable htmlFor="rate">Tax Rate</label>
                        <input [hidden]="!taxBool()" #highlightable pInputText id="rate" type="number" formControlName="TaxRate" [ngClass]="{'highlight': highlightedFields.has('rate')}" class="w-full"/>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                        <label [hidden]="!taxBool()" #highlightable htmlFor="code">Tax Code</label>
                        <input [hidden]="!taxBool()" #highlightable pInputText id="code" type="number" formControlName="Location_Code" [ngClass]="{'highlight': highlightedFields.has('code')}" class="w-full"/>
                    </div>
                </div>
            </div>
        </div>
    <div class="col-12">
            <div class="p-fluid p-formgrid grid">
                <div class="flex mt-8">
            <div class="card flex flex-col gap-4 w-full">
                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="wind">Wind Load</label>
                        <p-inputgroup class="w-full">
                        <input pInputText id="wind" type="number" [max]="200" [min]="0" formControlName="windload" class="w-full"/>
                        <p-inputgroup-addon>
                        
                        <p-selectButton 
                        [allowEmpty]="false"
                        size="small"
                          [options]="engineeringFlaggingService.windTypes()" 
                          formControlName="windType"
                          class="flex-none"/>
</p-inputgroup-addon>
                        </p-inputgroup>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="snow">Snow Load</label>
                    <p-inputgroup class="w-full">
                    <input pInputText id="snow" type="number" formControlName="snowload" [max]="200" [min]="0" class="flex-1"/>
                    <p-inputgroup-addon>
                        <p-selectButton 
                        size="small"
                        [allowEmpty]="false"
                            [options]="engineeringFlaggingService.snowTypes()" 
                            formControlName="snowType"
                            class="flex-none"/>
                        </p-inputgroup-addon>
                    </p-inputgroup>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="frost">Frost Depth</label>
                    <input pInputText id="frost" type="number" formControlName="FrostDepth" [max]="300" pSize="large" [min]="0" class="w-full">
                    </div>
                </div>
                <div class="flex flex-col md:flex-row gap-4">
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="exposure">Exposure</label>
                        <p-select [options]="engineeringFlaggingService.exposureOptions()" 
                    formControlName="Exposure"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select an Exposure"
                    class="w-full"
                    appendTo="body"
                    ></p-select>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="seismic">Seismic</label>
                        <p-select [options]="engineeringFlaggingService.seismicOptions()" 
                    formControlName="Seismic"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a Seismic Zone"
                    class="w-full"
                    appendTo="body"
                    ></p-select>
                    </div>
                    <div class="flex flex-wrap gap-2 w-full">
                    <label htmlFor="rSnow">Soil PSF</label>
                    <input pInputText id="rSnow" type="number" formControlName="SoilPSF" [max]="9000" [min]="0" [ngClass]="{'highlight': engineeringFlaggingService.isAbnormal('soil', editForm.value['SoilPSF'])}" class="w-full"/>
                    </div>
                </div>
            </div>
        </div>

            </div>
        </div>
    </div>


          <div class="flex flex-col sm:flex-row w-full gap-4">
        <div class="info ml-2 md:mr-2 mt-2">
            <span class="block text-900 font-medium mb-1 text-xl" *ngIf="changeCount === 1"><h5>{{ changeCount }} Change</h5></span>
            <span class="block text-900 font-medium mb-1 text-xl" *ngIf="changeCount > 1"><h5>{{ changeCount }} Changes</h5></span>
          </div>
        <button pButton pRipple icon="pi pi-file-pdf" [rounded]="false" [label]="'Save'" (click)="onSave()" [disabled]="!formChanged" class="w-auto ml-auto mt-2"></button>
        </div>

    `,
})
export class BinderSiteEditorComponent {

  fb = inject(FormBuilder);
  binderService = inject(BinderService);
  binderDialogService = inject(BinderDialogService);
  binderDeliverablesService = inject(BinderDeliverablesService);
  highlightService = inject(SiteFieldHighlightService);
  engineeringFlaggingService = inject(EngineeringFlaggingService);

  orientations: string[] = ["Standard (Garage Right)", "Reversed (Garage Left)", "Standard (No Garage)", "Reversed (No Garage)"]
  @ViewChildren('highlightable') highlightableFields!: QueryList<ElementRef>;

  defaultSoilPSF: number = 1500;

  warnings: Warning[] = [];

  highlightedFields: Set<string> = new Set();
  initialFormValues: any = {};
  changeCount: number = 0;
  saved: boolean = true;
  editForm: FormGroup;
  selectedState: string = '';
  loading: boolean = false;
  formChanged: boolean = false;
  taxBool!: ReturnType<typeof toSignal<boolean>>;
  taxModes: any[] = [
    {
      label: 'Auto',
      value: false
    },
    {
      label: 'Manual',
      value: true
    }
  ];
  countFields: boolean = false;
  private isInitialLoad: boolean = true;

  constructor(
    
  ) {
    this.editForm = this.fb.group({
      SoilPSF: [this.defaultSoilPSF],
      Exposure: [''],
      Seismic: [null],
      FrostDepth: [null],
      SitePlot: [''],
      Orientation: [''],
      SiteAddress: [''],
      SiteCity: [''],
      SiteState: [''],
      SiteZip: [null],
      TaxOverride: [false],
      TaxRate: [null],
      Location_Code: [null],
      County: [''],
      StateID: [0],
      windType: [null],
      windload: [0],
      snowType: [null],
      snowload: [0]
    });
    this.taxBool = toSignal(this.editForm.get('TaxOverride')!.valueChanges, { initialValue: this.editForm.get('TaxOverride')!.value });
    effect(() => {
      const binder = this.binderService.currentBinder();
      // const fieldsToHighlight = this.highlightService.getHighlightSignal()();

      // if (fieldsToHighlight.includes('rate') || fieldsToHighlight.includes('code')) {
      //   // this.highlightTaxFields();
      // } else {
      //   this.highlightFields(fieldsToHighlight);
      // }

      if (binder) {
        this.countFields = false;
        this.editForm.patchValue(binder);
        this.initialFormValues = { ...this.editForm.value }; // Store initial values
        this.countFields = true;
        this.isInitialLoad = false; // Set flag to false after initial load
        if (binder.SiteState) {
          this.setSelectedState(binder.SiteState);
        }
      }
    });
  }

  get states() {
    return states;
  }

  // highlightTaxFields() {
  //   // First ensure tax fields are visible
  //   if (!this.taxBool) {
  //     // Find the "Override" option in taxModes
  //     const overrideMode = this.taxModes.find(mode => mode.value === true);
  //     if (overrideMode) {
  //       this.editForm.get('TaxOverride')?.setValue(true);
  //     }
  //   }

  //   setTimeout(() => {
  //     this.highlightFields(['rate', 'code']);
  //   });
  // }

  onEditWind() {
    const value = this.editForm.value['windload'];
    if (this.editForm.value['windType'] === 'ASD') {
      const ULT = this.convertASDtoULT(value);
      this.editForm.get('WindloadULT')?.setValue(ULT);
      this.editForm.get('WindloadASD')?.setValue(value);
    } else {
      const ASD = this.convertULTtoASD(value);
      this.editForm.get('WindloadASD')?.setValue(ASD);
      this.editForm.get('WindloadULT')?.setValue(value);
    }
  }

  convertULTtoASD(value: number) {
    return Math.round(value * Math.sqrt(0.6));
  }

  convertASDtoULT(value: number) {
    return Math.round(value / Math.sqrt(0.6));
  }

  ngOnInit() {
    this.editForm.valueChanges.subscribe(() => {
      if (this.countFields && !this.isInitialLoad) { // Check the flag
        this.formChanged = true;
        this.countChangedFields();
      }
    });
  }

  getWarnings() {
    const warnings: Warning[] = [];
    if (this.engineeringFlaggingService.isAbnormal('soil', this.editForm.value['SoilPSF'])) {
      warnings.push({ severity: 'warn', text: 'Soil PSF is above normal' });
    }
    if (this.engineeringFlaggingService.isAbnormal('wind', this.editForm.value['windload'], this.editForm.value['windType'])) {
      warnings.push({ severity: 'warn', text: 'Wind Load is above normal' });
    }
    if (this.engineeringFlaggingService.isAbnormal('snow', this.editForm.value['snowload'], this.editForm.value['snowType'])) {
      warnings.push({ severity: 'warn', text: 'Snow Load is above normal' });
    }
    if (this.engineeringFlaggingService.isAbnormal('frost', this.editForm.value['FrostDepth'])) {
      warnings.push({ severity: 'warn', text: 'Frost Depth is above normal' });
    }
    if (this.engineeringFlaggingService.isAbnormal('exposure', this.editForm.value['Exposure'])) {
      warnings.push({ severity: 'warn', text: 'Exposure is above normal' });
    }
    if (this.engineeringFlaggingService.isAbnormal('seismic', this.editForm.value['Seismic'])) {
      warnings.push({ severity: 'warn', text: 'Seismic is above normal' });
    }
    this.warnings = warnings;
  }


  highlightFields(fieldsToHighlight: string[]) {
    // Reset previous highlights
    this.highlightedFields.clear();

    // Add new fields to highlight
    fieldsToHighlight.forEach(field => this.highlightedFields.add(field));
  }

  setSelectedState(state: string) {
    this.selectedState = state;
    console.log('selectedState', this.selectedState);
    const stateId = states.find(state => state.shortName === this.selectedState)?.StateID;
    this.editForm.patchValue(
      {
        StateID: stateId,
        SiteState: this.selectedState
      }
    );
  }

  checkWind() {
    const wind = this.editForm.get('WindLoadASD')?.value;
    if (wind) {
      this.editForm.get('windType')?.setValue('ASD');
    } else {
      this.editForm.get('windType')?.setValue('ULT');
    }
  }

  countChangedFields() {
    this.changeCount = Object.keys(this.editForm.value).reduce((count, key) => {
      if (this.editForm.value[key] !== this.initialFormValues[key]) {
        return count + 1;
      }
      return count;
    }, 0);
  }

  onSave() {
    this.loading = true;
    if (!this.saved) {
      this.binderDeliverablesService.saveChanges(this.binderService.currentBinder().BinderID).subscribe({
        next: (updatedDeliverables) => {
          console.log('Deliverables saved successfully');
          this.updateBinder();
          this.changeCount = 0;
        },
        error: (error) => {
          console.error('Error saving deliverables:', error);
          this.handleSaveError('deliverables');
        }
      });
    } else {
      this.updateBinder();
    }
  }

  private updateBinder() {
    if (this.editForm.valid) {
      this.binderService.updateBinder(this.binderService.currentBinder().BinderID, this.editForm.value).subscribe({
        next: (updatedBinder) => {
          console.log('form values', this.editForm.value);
          this.binderService.patchBinder(updatedBinder);
          this.loading = false;
          this.binderDialogService.toggleDialog('', 'docs', false);
          // this.closeDialog.emit();
        },
        error: (error) => {
          console.error('Error updating binder:', error);
          this.handleSaveError('binder');
        }
      });
    } else {
      this.loading = false;
      this.handleSaveError('validation');
    }
  }

  private handleSaveError(errorType: 'deliverables' | 'binder' | 'validation') {
    this.loading = false;
    let errorMessage = '';
    switch (errorType) {
      case 'deliverables':
        errorMessage = 'Failed to save deliverables. Please try again.';
        break;
      case 'binder':
        errorMessage = 'Failed to update binder. Please try again.';
        break;
      case 'validation':
        errorMessage = 'Please fill in all required fields correctly.';
        break;
    }
    console.error(errorMessage);

    // this.closeDialog.emit({ error: true, message: errorMessage });
  }

}
